import {observer} from 'mobx-react-lite'
import React, {useState} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {AvatarGroupBase} from '@components/common/base/avatar'
import {IconButton} from '@components/common/base/buttons'
import {Menu} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {useIdTaskUpdater} from '@components/id-tasks/use-id-task-updater'
import {keyboardHandler, noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {LabelBackgrounded} from '../label/label-backgrounded'
import {
  TwHeader,
  TwProjectCard,
  TwTitle,
  TwTitleInput,
  TwUsers
} from './containers'
import {ProjectCardProps} from './types'
import {useTitleEdit} from './use-title-edit'

const draggedStyle: React.CSSProperties = {
  transition: '0.01s',
  transform: 'translateX(-9999px)'
}

export const ProjectCard = observer((props: ProjectCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const {
    inputRef,
    title,
    setTitle,
    onCancel: onTitleEditCancel,
    onSubmit: onTitleEditSubmit
  } = useTitleEdit(props)
  // Custom hover to prevent hover jumps during DnD
  // todo: try remove custom hover when refactoring to custom DnD
  const hoverable = props.hoverable ?? true
  const [isHover, setIsHover] = useState(false)
  const showMenu =
    !!props.menuItems.length &&
    !props.menuItems.every(x => 'disabled' in x && x.disabled)

  const idProject = idTasksApi.getIdProject(props.id)
  const settings = CompanyPreferencesClient.get(globals.getCompanyId())
  const {useOldId} = settings

  useIdTaskUpdater()

  return (
    <TwProjectCard
      data-test="ProjectCard"
      className={props.className}
      title={props.title}
      onClick={e => props.onClick?.(props.id, e)}
      draggable={props.draggable}
      onDragStart={e => props.onDragStart?.(props.id, e)}
      onDragEnd={e => props.onDragEnd?.(props.id, e)}
      onDragEnter={e => props.onDragEnter?.(props.id, e)}
      $hover={hoverable && isHover}
      onMouseEnter={() => hoverable && setIsHover(true)}
      onMouseMove={() => hoverable && setIsHover(true)}
      onMouseLeave={() => hoverable && setIsHover(false)}
      style={props.dragged ? draggedStyle : undefined}
    >
      <TwHeader>
        {props.titleEditing ? (
          <TwTitleInput
            ref={inputRef}
            autoFocus
            onClick={noPropagate}
            onBlur={onTitleEditSubmit}
            onKeyDown={keyboardHandler({
              enter: onTitleEditSubmit,
              escape: () => {
                onTitleEditCancel()
                return true
              }
            })}
            value={title}
            onChange={e =>
              setTitle(Util.removeLeadingString(e.target.value, '.'))
            }
            placeholder={Lang.msg.projectPlaceholder}
          />
        ) : (
          <TwTitle
            data-test="ProjectTitle"
            $alien={!props.titleEditing && props.alien}
            $hover={hoverable && isHover}
          >
            {props.title}
          </TwTitle>
        )}
        {showMenu && (
          <Dropdown
            hideArrow
            theme="new-menu"
            content={
              <Menu
                items={props.menuItems}
                onItemClicked={() => {
                  setIsMenuOpen(false)
                  setIsHover(false)
                }}
              />
            }
            visible={isMenuOpen}
            onVisibleChange={() => {
              setIsMenuOpen(false)
              setIsHover(false)
            }}
          >
            <div
              onClick={e => {
                e.stopPropagation()
                setIsMenuOpen(true)
              }}
              className="flex-none h-16 w-16 flex items-center justify-center"
            >
              <IconTabler
                customSize={16}
                icon="IconDotsVertical"
                viewBox="0 0 24 24"
                className={cn(
                  !isHover && !props.titleEditing && props.alien
                    ? 'text-grey-l40'
                    : 'text-grey',
                  'hover:text-grey-l20',
                  'active:text-grey-d20'
                )}
              />
            </div>
          </Dropdown>
        )}
      </TwHeader>

      <div className="flex gap-6">
        {!useOldId && <LabelBackgrounded text={idProject} className={'mt-4'} />}

        {!useOldId && props.isCrmProject && (
          <LabelBackgrounded text="CRM" className={'mt-4'} />
        )}
      </div>

      <TwUsers
        onClick={e => {
          e.stopPropagation()
          props.onAddUserClick?.(props.id, e)
        }}
        className="mt-auto"
      >
        <AvatarGroupBase
          users={props.users}
          size={16}
          className="flex-none mr-4"
          outlineColor="grey-l100"
          disabled={props.alien && !isHover}
          hideStatus
        />
        {props.onAddUserClick && (
          <IconButton
            icon="IconCirclePlus"
            size="small"
            color="primary"
            className={cn(
              'flex-none',
              props.alien && !isHover && '!text-interactive-l80'
            )}
            title={Lang.msg.addUser}
          />
        )}
      </TwUsers>
    </TwProjectCard>
  )
})
